import React, { useEffect, useState } from 'react';

import { Box, colors, Link, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ImageAvatar } from 'components/Avatar';
import { ListCard } from 'components/Card';

import { isoToDateString } from 'utils/dateFormatter';
import { paths } from 'routes';
import { useLocale } from 'i18n/useLocale';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en'; // Import a base locale, for example 'en'
import { IconWrapper } from 'components/Icon';
import { localizeRelativeTime } from 'i18n/dayjs';
import { formatJobAdLocation, formatJobAdLocationShort, getJobAdLocations } from 'utils/jobAdLocations';

dayjs.extend(relativeTime);
dayjs.extend(utc);

// Get the existing 'en' locale data
const enLocale = dayjs.Ls.en;

// Define a custom locale to remove "ago"
const customRelativeTime = {
  ...enLocale, // Start with the full 'en' locale data
  relativeTime: {
    ...enLocale.relativeTime, // Extend existing relativeTime if needed
    future: "in %s",
    past: "%s", // Changed from "%s ago" to "%s"
    s: "a few seconds",
    m: "a minute",
    mm: "%d minutes",
    h: "an hour",
    hh: "%d hours",
    d: "a day",
    dd: "%d days",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years"
  }
};

dayjs.locale('custom', customRelativeTime); // Register and use the custom locale

const userTimeZone = dayjs.tz.guess();

const getFirstSentence = (text) => {
  if (!text) return '';
  const firstSentenceMatch = text.match(/[^.!?]+[.!?]/);
  return firstSentenceMatch ? firstSentenceMatch[0].trim() : text.trim();
};

export const ListItem = ({
  ad_id,
  headline,
  slug,
  buyer_id,
  buyer_name,
  buyer_slug,
  supplier_id,
  buyer_logo_url,
  locations,
  places,
  supplier_name,
  supplier_slug,
  supplier_logo_url,
  last_day_to_respond,
  role,
  competence_areas,
  created_on,
  description,
  owner_type,
}) => {
  const { t, i18n } = useTranslation();
  const lang = useLocale();
  const adjustedDate = localizeRelativeTime(dayjs.utc(created_on).tz(userTimeZone, true), i18n.language);
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeAgo(adjustedDate.fromNow());
    }, 60000);

    setTimeAgo(adjustedDate.fromNow());

    return () => clearInterval(intervalId);
  }, [adjustedDate]);

  const displayLocations = getJobAdLocations({ locations, places });

  const locationsTooltip = (
    <>
      {displayLocations.map((loc) => (
        <div key={loc.id}>{formatJobAdLocation(loc)}</div>
      ))}
    </>
  );

  const locationsBaseText = displayLocations.length === 1 ? formatJobAdLocationShort(displayLocations[0]) : 'Multiple';

  let locationsDisplayText;
  if (locationsBaseText.length > 10) {
    locationsDisplayText = `${locationsBaseText.slice(0, 10)}...`;
  } else {
    locationsDisplayText = `${locationsBaseText}`;
  }

  const areasTooltip = (
    <>
      {competence_areas?.map((area) => (
        <div key={area.id}>{area.name}</div>
      ))}
    </>
  );

  const areasBaseText = competence_areas ? competence_areas.length === 1 ? `${competence_areas[0]?.name}` : 'Multiple' : '';

  let areasDisplayText;
  if (areasBaseText.length > 15) {
    areasDisplayText = `${areasBaseText.slice(0, 15)}...`;
  } else {
    areasDisplayText = `${areasBaseText}`;
  }

  return (
    <ListCard
      addDefaultStyling = {false}
      sx={{
        borderRadius: 1,
        borderColor:"border.jobAd",
        flexDirection: "row",
        p: 2,
        gap: 2,
        boxShadow: 'none',
        alignItems: "center",
        flex: "1 0 0",
      }}
    >
      {/* <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
          flex: "1 0 0",
        }}
      > */}
        {/* <Stack
          sx={{
            flexDirection: "row",
            alignItems: "flex-start",
            gap: {md: 2, lg: 5},
            '& > *': {
              borderRight: '2px solid',
              borderColor: 'border.main',
              pr: {md: 2, lg: 5},
            },
            display: {xs: "none", md: "flex"}
          }}
        >
          <Stack
            sx={{
              flexDirection: "column",
              gap: 0.375,
              height: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.black"
              fontWeight={500}
              letterSpacing={0}
            >
              APPLY BEFORE
            </Typography>
            <Typography
              variant="body3"
              color="text.custom"
              letterSpacing={0}
              lineHeight={"200%"}
            >
              {isoToDateString(last_day_to_respond, false)}
            </Typography>
          </Stack>
          <Stack
            sx={{
              flexDirection: "column",
              gap: 0.375,
              height: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.black"
              fontWeight={500}
              letterSpacing={0}
            >
              COMPETENCE AREAS
            </Typography>
            {!competence_areas || competence_areas?.length === 0 
            ? 
              <Typography
                variant="body3"
                color="text.custom"
                letterSpacing={0}
                lineHeight={"200%"}
              >
              </Typography>
            :
              <Tooltip title={areasTooltip}>
                <Typography
                  variant="body3"
                  color="text.custom"
                  letterSpacing={0}
                  lineHeight={"200%"}
                >
                  {areasDisplayText}
                </Typography>
              </Tooltip>
            }
          </Stack>
          <Stack
            sx={{
              flexDirection: "column",
              gap: 0.375,
              height: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.black"
              fontWeight={500}
              letterSpacing={0}
            >
              LOCATIONS
            </Typography>
            {displayLocations.length <= 0 
            ? 
              <Typography
                variant="body3"
                color="text.custom"
                letterSpacing={0}
                lineHeight={"200%"}
              >
              </Typography>
            :
              <Tooltip title={locationsTooltip}>
                <Typography
                  variant="body3"
                  color="text.custom"
                  letterSpacing={0}
                  lineHeight={"200%"}
                >
                  {locationsDisplayText}
                </Typography>
              </Tooltip>
            }
          </Stack>
          <Stack
            sx={{
              flexDirection: "column",
              gap: 0.375,
              height: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.black"
              fontWeight={500}
              letterSpacing={0}
            >
              PUBLISHED
            </Typography>
            <Typography
              variant="body3"
              color="text.custom"
              letterSpacing={0}
              lineHeight={"200%"}
            >
              {timeAgo}
            </Typography>
          </Stack>
        </Stack> */}
        {/* <Stack
          sx={{
            flexDirection: "row",
            display: "flex",
            gap: 2,
            width: 1,
            alignItems: "center",
            flex: "1 0 0"
          }}
        > */}
          {buyer_slug?.length > 0 ? (
            <Link
              component={RouterLink}
              to={`${paths.public.companyPublicProfile("buyer", buyer_slug, lang)}`}
              color="inherit"
              underline="none"
            >
              <ImageAvatar img={buyer_logo_url || ''} name={buyer_name || ''}
                sx={{
                  width: {xs: 30, md: 42},
                  height: {xs: 30, md: 42},
                }}
              />
            </Link>
          ) : (
            <ImageAvatar img={buyer_logo_url || ''} name={buyer_name || ''}
              sx={{
                width: {xs: 30, md: 42},
                height: {xs: 30, md: 42},
                img : {
                  width: {xs: 30, md: 42},
                  height: {xs: 30, md: 42},
                  overflow: 'hidden',
                }
              }}
            />
          )}
            <Link
              component={RouterLink}
              to={`${paths.public.jobAdsDetailsView(slug, lang)}`}
              color="inherit"
              underline="none"
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: 1,
                gap: 2,
                flex: "1 0 0",
              }}
            >
              <Stack flexDirection={"column"} width={1} flex={"1 0 0"}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFeatureSettings: "'liga' off, 'cling' off",
                    fontSize: {xs: "0.875rem", md: "1.25rem"},
                    fontWeight: {xs: 500, md: 700},
                    lineHeight: 1.6,
                    color: "text.black",
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1,
                    overflow: 'hidden',
                  }}
                >
                  {headline}
                </Typography>
                <Typography
                  variant="body3"
                  sx={{
                    fontFeatureSettings: "'liga' off, 'cling' off",
                    fontSize: {xs: "0.75rem", md: "0.875rem"},
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "text.list",
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1,
                    overflow: 'hidden',
                  }}
                >
                  {getFirstSentence(description)}
                </Typography>
              </Stack>
            </Link>
          {/* </Stack> */}
        {/* </Stack> */}
        {/* <Typography
          variant="body3"
          fontWeight={400}
          color='text.list'
        >
          {getFirstSentence(description)}
        </Typography> */}
      {/* </Stack> */}
      <Stack
        justifyContent={"flex-end"}
        alignItems={{xs: "center", md: "flex-end"}}
        flexDirection={{xs: "row", md: "column"}}
        sx={{
          gap: 1.375,
          minWidth: 0,
        }}
      >
        <Stack
          sx={{
            flexDirection: {xs: "column", md: "row"},
            alignItems: {xs: "flex-start", md: "center"},
            gap: {xs: 0.5, md: 1.375},
            minWidth: 0,
            flex: {xs: "1 1 auto", md: "0 1 auto"},
          }}
        >
          {owner_type && (
            <Box
              sx={{
                gap: 0.75,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
                maxWidth: 1,
              }}
            >
              <IconWrapper
                icon="briefcase"
                sx={{
                  height: {xs: 14, md: 18},
                  width: {xs: 14, md: 18},
                  flexShrink: 0,
                  svg: {
                    stroke: "#000",
                    strokeWidth:1,
                    height: {xs: 14, md: 18},
                    width: {xs: 14, md: 18},
                  }
                }}
                height={18}
              />
              <Typography
                variant="body3"
                sx={{
                  color: "text.black",
                  fontSize: {xs: "0.75rem", md: "0.875rem"},
                  fontWeight: 500,
                  lineHeight: "20px",
                  minWidth: 0,
                  maxWidth: {xs: 100, md: "none"},
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t(`website:jobAdCard.ownerTypes.${owner_type}`, { defaultValue: owner_type })}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              gap: 0.75,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0,
              maxWidth: 1,
            }}
          >
            <IconWrapper
              icon="tag"
              sx={{
                height: {xs: 14, md: 18},
                width: {xs: 14, md: 18},
                flexShrink: 0,
                svg: {
                  stroke: "#000",
                  strokeWidth:1,
                  height: {xs: 14, md: 18},
                  width: {xs: 14, md: 18},
                }
              }}
              height={18}
            />
            <Typography
              variant="body3"
              sx={{
                color: "text.black",
                fontSize: {xs: "0.75rem", md: "0.875rem"},
                fontWeight: 500,
                lineHeight: "20px",
                minWidth: 0,
                maxWidth: {xs: 100, md: "none"},
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {buyer_name}
            </Typography>
          </Box>
          <Box
            sx={{
              gap: 0.75,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconWrapper
              icon="clock"
              sx={{
                height: {xs: 14, md: 18},
                width: {xs: 14, md: 18},
                svg: {
                  stroke: "#000",
                  strokeWidth:1,
                  height: {xs: 14, md: 18},
                  width: {xs: 14, md: 18},
                }
              }}
              height={18}
            />
            <Typography
              variant="body3"
              sx={{
                color: "text.black",
                fontSize: {xs: "0.75rem", md: "0.875rem"},
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              {timeAgo}
            </Typography>
          </Box>
        </Stack>
        <Box
          sx={{
            width: {xs: 20, md: 24},
            height: {xs: 20, md: 24},
            alignSelf: {xs: 'center', md: 'flex-end'},
            flexShrink: 0,
          }}
        >
        {supplier_slug?.length > 0 ? (
          <Link
            component={RouterLink}
            to={`${paths.public.companyPublicProfile("supplier", supplier_slug, lang)}`}
            color="inherit"
            underline="none"
          >
            <ImageAvatar img={supplier_logo_url || ''} name={supplier_name || ''} fontSize='1rem'
              sx={{
                width: {xs: 20, md: 24},
                height: {xs: 20, md: 24},
                img : {
                  width: {xs: 20, md: 24},
                  height: {xs: 20, md: 24},
                  overflow: 'hidden',
                },
                fontSize: {xs: "0.875rem", md: "1rem"}
              }}
            />
          </Link>
        ) : (
          <ImageAvatar img={supplier_logo_url || ''} name={supplier_name || ''} fontSize='1rem'
            sx={{
              width: {xs: 20, md: 28},
              height: {xs: 20, md: 28},
              img : {
                width: {xs: 20, md: 28},
                height: {xs: 20, md: 28},
                overflow: 'hidden',
              },
              fontSize: {xs: "0.875rem", md: "1rem"}
            }}
          />
        )}
        </Box>
      </Stack>
    </ListCard>
  );
};
